/* istanbul ignore file */
import { useInputHelper, ClearAfterUnmountWrapper } from 'framework/form';
import CostReportSourceBadge from './CostReportSourceBadge';
import useMetricsHelper from 'domain/finance/costAccounting/shared/metricsHelper';
import { BigSectionRow, MediumFormRow } from 'framework/ui/layout';
import { useEffect, useState } from 'react';
import useCostAccountingService, {
  MappingMetricsType,
} from 'domain/finance/costAccounting/shared/costAccountingService';
import { Loading } from 'framework/ui/mui-builder';
import MappingMetricsTypeInput from 'domain/finance/costAccounting/dashboard/MappingMetricsTypeInput';
import MappingMetrics from 'domain/finance/costAccounting/dashboard/MappingMetrics';
import { COST_REPORT_SOURCE_OPTIONS } from './costReportSource';

interface MappingMetricsProps {
  fromDateSource: string;
  toDateSource: string;
}

type MetricsData = {
  sourceType: keyof typeof COST_REPORT_SOURCE_OPTIONS | string;
  mappedItemsCount: number;
  totalItemsCount: number;
  mappedHoursSum: number;
  totalHoursSum: number;
};

const MappingMetricsWrapper = ({ fromDateSource, toDateSource }: MappingMetricsProps) => {
  const { getMappingMetrics } = useCostAccountingService();
  const { isDateValid } = useMetricsHelper();
  const { getValue: getFromDate } = useInputHelper({ source: fromDateSource });
  const { getValue: getToDate } = useInputHelper({ source: toDateSource });
  const fromDate = getFromDate() as string;
  const toDate = getToDate() as string;

  const [metricsVisible, setMetricsVisible] = useState(false);
  const [metricsLoading, setMetricsLoading] = useState(true);
  const [metricsData, setMetricsData] = useState<MetricsData>({
    sourceType: '',
    mappedItemsCount: 0,
    totalItemsCount: 0,
    mappedHoursSum: 0,
    totalHoursSum: 0,
  });

  useEffect(() => {
    const bothDatesValid = isDateValid(fromDate) && isDateValid(toDate);
    setMetricsVisible(bothDatesValid);
    if (bothDatesValid) {
      setMetricsLoading(true);
      getMappingMetrics({ fromDate, toDate }).then(response => {
        setMetricsData(response as MetricsData);
        setMetricsLoading(false);
      });
    }
  }, [fromDate, toDate]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!metricsVisible) {
    return (
      <div>
        <BigSectionRow>
          <span>Please ensure that you fill out the form above accurately in order to view the mapping metrics.</span>
        </BigSectionRow>
      </div>
    );
  }

  if (metricsLoading) {
    return (
      <div>
        <Loading />
      </div>
    );
  }

  return (
    <div>
      <CostReportSourceBadge sourceType={metricsData.sourceType} />

      <MediumFormRow>
        <MappingMetricsTypeInput source="metricsType" />
      </MediumFormRow>
      <MediumFormRow>
        <ClearAfterUnmountWrapper isVisible={data => data.metricsType === MappingMetricsType.METRICS_BY_RECORDS}>
          <MappingMetrics
            current={metricsData.mappedItemsCount}
            expected={metricsData.totalItemsCount}
            measurementName="Records"
          />
        </ClearAfterUnmountWrapper>
        <ClearAfterUnmountWrapper isVisible={data => data.metricsType === MappingMetricsType.METRICS_BY_HOURS}>
          <MappingMetrics
            current={metricsData.mappedHoursSum}
            expected={metricsData.totalHoursSum}
            measurementName="Hours"
          />
        </ClearAfterUnmountWrapper>
      </MediumFormRow>
    </div>
  );
};

export default MappingMetricsWrapper;
